// Package imports:
import { format, lastDayOfMonth, subMonths } from "date-fns";
// Type imports:
import { KeldanDate } from "../types/CompanyTypes";

export const formatNumber = (
    num: number | null | undefined,
    fallBackSymbol: string | null = '-',
    fractionDigits: number = 2,
    suffix = ''
) => {
    if (num === null || num === undefined || isNaN(num)) {
        return fallBackSymbol;
    }

    const formattedNumber = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    }).format(num);
    return formattedNumber + suffix;
}

export function convertToPercentage(num: number | null, displayPercentageSign: boolean, fractionDigits: number = 2) {
    if (num === null) {
        return '-';
    }
    num = num * 100;

    if (displayPercentageSign) {
        return (formatNumber(num, null, fractionDigits) + '%');
    } else {
        return (formatNumber(num, null, fractionDigits));
    }

}

export function displayQuantityShorthand(quantity: number | null, fractionDigits?: number): string {
    if (quantity === null) {
        return '-';
    }
    if (quantity < 0) {
        return `-${displayQuantityShorthand(-quantity)}`
    }
    //check if its 1b or more
    if (quantity >= 1000000000) {
        return ((quantity / 1000000000).toFixed(fractionDigits ?? 1) + 'ma');
    } else if (quantity >= 1000000) {
        return ((quantity / 1000000).toFixed(fractionDigits ?? 0) + 'm');
    } else if (quantity >= 1000) {
        return ((quantity / 1000).toFixed(fractionDigits ?? 0) + 'þ')
    }
    return '0';
}

export function quantityShorthandSplit(quantity: number | null, fractionDigits?: number): { number: string | null, unit: string | null } {
    if (quantity === null) {
        return { number: null, unit: null }
    }
    if (quantity < 0) {

    }
    if (Math.abs(quantity) >= 1000000) {
        return {
            number: formatNumber(quantity / 1000000, null, fractionDigits ?? 1),
            unit: ' m'
        }
    } else if (Math.abs(quantity) >= 1000) {
        return {
            number: formatNumber(quantity / 1000, null, fractionDigits ?? 1),
            unit: ' þ'
        }
    }
    return {
        number: formatNumber(quantity, null, 0),
        unit: null
    }
}

export function displayPrice(price?: number): string {
    if (price === undefined) {
        return '-';
    }
    if (price < 0) {
        return `-${displayPrice(-price)}`
    }
    //check if its 1b or more
    if (price >= 1000000000) {
        return `${formatNumber(price / 1000000000, null, 3)} ma.kr.`;
    } else if (price >= 1000000) {
        return `${formatNumber(price / 1000000, null, 2)} m.kr.`;
    } else if (price >= 1000) {
        return `${formatNumber(price / 1000, null, 2)} þ.kr.`;
    }
    return '0';
}

export function calculateDelta(num: number) {
    num = num * 10000;
    if (Math.abs(num) === 0) {
        return '-';
    }

    if (0 <= Math.abs(num) && Math.abs(num) <= 0.5) {
        if(num < 0)
            return '-'+ Math.round(num);
        }
        else {
            return + Math.round(num);
        }
    return Math.round(num);
}

//Returns a string of the format YYYY-MM-DD
export function getAPItoday(date: Date = new Date()) {
    return format(date, 'yyyy-MM-dd');
}

export const getLastDayOfLastMonth = () => {
    const date = new Date();
    const lastDayOfLastMonth = lastDayOfMonth(subMonths(date, 1));
    const dateStringFormat = format(lastDayOfLastMonth, 'yyyy-MM-dd');
    return dateStringFormat;
}

//returns a string with prefered format
//if no format is provided it defaults to null
export function getTableDate(tableDate: string | Date | null, format: 'HH:MM' | 'HH:MM:SS' | 'DD/MM' | 'DD/MM/YY' | 'DD/MM/YYYY' | 'DD/MM/YYYY HH:MM' | 'DD/MM/YYYY HH:MM:SS', separator: '.' | '/' = '/') {
    if (tableDate === null) return '-';
    const date = (typeof tableDate === 'string')
        ? new Date(tableDate.replace(/ /g, "T"))
        : tableDate;

    if (date.getFullYear() < 1800) return '-';

    if (format === 'HH:MM') {
        if (isDateToday(date)) {
            return `${((date.getHours()).toString()).padStart(2,'0')}:${((date.getMinutes()).toString()).padStart(2,'0')}`;
        }
        return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${date.getFullYear()}`;
    }
    if (format === 'HH:MM:SS') {
        if (isDateToday(date)) {
            return `${((date.getHours()).toString()).padStart(2,'0')}:${((date.getMinutes()).toString()).padStart(2,'0')}:${((date.getSeconds()).toString()).padStart(2,'0')}`;
        }
        return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${date.getFullYear()}`;
    }
    if (format === 'DD/MM') {
        return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator === '.' ? separator : ''}`;
    }

    if (format === 'DD/MM/YY') {
        return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${((date.getFullYear() % 100).toString()).padStart(2,'0')}`;
    }

    if (format === 'DD/MM/YYYY') {
        return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${date.getFullYear()}`;
    }

    if (format === 'DD/MM/YYYY HH:MM') {
        return ((date.getDate()).toString()).padStart(2,'0')
            + separator
            + ((date.getMonth() + 1).toString()).padStart(2,'0')
            + separator
            + date.getFullYear()
            + ' '
            + ((date.getHours()).toString()).padStart(2,'0')
            + ':'
            + ((date.getMinutes()).toString()).padStart(2,'0');
    }
    if (format === 'DD/MM/YYYY HH:MM:SS') {
        return ((date.getDate()).toString()).padStart(2,'0')
            + separator
            + ((date.getMonth() + 1).toString()).padStart(2,'0')
            + separator
            + date.getFullYear()
            + ' '
            + ((date.getHours()).toString()).padStart(2,'0')
            + ':'
            + ((date.getMinutes()).toString()).padStart(2,'0')
            + ':'
            + ((date.getSeconds()).toString()).padStart(2,'0');
    }
    return '-';
}

export function getKeldanDateString(d: KeldanDate | null, format: 'HH:MM' | 'HH:MM:SS' | 'DD/MM' | 'DD/MM/YY' | 'DD/MM/YYYY', separator: '.' | '/' = '/') {
    if (d === null) return '-';
    const date = new Date(d.year+1900, d.month, d.date, d.hours, d.minutes, d.seconds);
    switch (format) {
        case 'HH:MM':
            if (isDateToday(date)) {
                return `${((date.getHours()).toString()).padStart(2,'0')}:${((date.getMinutes()).toString()).padStart(2,'0')}`;
            }
            return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${date.getFullYear()}`;
        case 'HH:MM:SS':
            if (isDateToday(date)) {
                return `${((date.getHours()).toString()).padStart(2,'0')}:${((date.getMinutes()).toString()).padStart(2,'0')}:${((date.getSeconds()).toString()).padStart(2,'0')}`;
            }
            return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${date.getFullYear()}`;
        case 'DD/MM':
            return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator === '.' ? separator : ''}`;
        case 'DD/MM/YY':
            return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${((date.getFullYear() % 100).toString()).padStart(2,'0')}`;
        case 'DD/MM/YYYY':
            return `${((date.getDate()).toString()).padStart(2,'0')}${separator}${((date.getMonth() + 1).toString()).padStart(2,'0')}${separator}${date.getFullYear()}`;
        default:
            return '-';
    }
}
export function getFullDateAndTime(date: Date): string{
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'NAN'
    }
    
    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
    };
    
    return date.toLocaleDateString('is-IS', options);
}

export function getFullMonthAndYear(date: Date){
    const MONTHS = ['Janúar', 'Febrúar', 'Mars', 'Apríl', 'Maí', 'Júní', 'Júlí', 'Ágúst', 'September', 'Október', 'Nóvember', 'Desember'];
    return MONTHS[date.getMonth()] + ' ' + date.getFullYear();
}

export function getTimeFromDate(date: string | null, format: "HH:MM" | "HH:MM:SS" = "HH:MM:SS", separator: ':' = ':') {
    if (date === null) return null;
    const newDate = new Date(date.replace(/ /g, "T"));
    
    if(format === "HH:MM"){
        return `${((newDate.getHours()).toString()).padStart(2,'0')}${separator}${((newDate.getMinutes()).toString()).padStart(2,'0')}`;
    }
    return `${((newDate.getHours()).toString()).padStart(2,'0')}${separator}${((newDate.getMinutes()).toString()).padStart(2,'0')}${separator}${((newDate.getSeconds()).toString()).padStart(2,'0')}`;
}

export const changeIssuerName = (name: string | null) => {
    const issuerToAvatarCode: {
        [T in string]: string
    } = {
        'Íslandssjóðir':'IS',
        'AKTA Sjóðir':'AKTA',
        'Kvika eignastýring':'KVIKAE',
        'Íslensk Verðbréf':'IV',
        'Landsbréf':'LANDSBREF',
        'Stefnir':'STEFNIR',
        'Frjálsi lífeyrissjóðurinn':'FRJALSI',
        'SIV eignastýring': 'SIV',
        // Sereignarsjodir:
        'Íslenski lífeyrissjóðurinn':'LAIS',
        'Íslandsbanki':'ISB'
    };
    return issuerToAvatarCode[name ?? ''] ?? name;
}

export const getLogoSymbol = (s: string) => {
    if(s === 'ICEAIRW180222' || s === 'ICEAIRW130821' || s=== 'ICEAIRW120822') return 'ICEAIR';
    if (s === 'LEQ') return 'LANDSBREF';
    if (s === 'ARION SDB') return 'ARION';
    return s;
}

export function isDateToday(date: Date) {
    const today = new Date();

    return (
        (date.getDate() === today.getDate())
        && (date.getMonth() === today.getMonth())
        && (date.getFullYear() === today.getFullYear())
    );
}

export function getNewsDateOrTime(date: Date) {
    const now = new Date();
    const isCurrentYear = (date.getFullYear() === now.getFullYear());
    // If today, use time
    if (
        date.getDate() === now.getDate()
        && date.getMonth() === now.getMonth()
        && isCurrentYear
    ) {
        return (
            <strong>
                {((date.getHours()).toString()).padStart(2, '0')
                + ':'
                + ((date.getMinutes()).toString()).padStart(2, '0')}
            </strong>
        );
    }
    // If in past, use date
    else if (isCurrentYear) {
        return (
            ((date.getDate()).toString()).padStart(2, '0')
            + '.'
            + ((date.getMonth() + 1).toString()).padStart(2, '0')
            + '.'
        );
    } else {
        return (
            ((date.getDate()).toString()).padStart(2, '0')
            + '.'
            + ((date.getMonth() + 1).toString()).padStart(2, '0')
            + '.'
            + ((date.getFullYear()).toString()).slice(2)
        );
    }
}

export const isElementSubChild = (allegedChild: Element, allegedParent: Element) => {
    const rootElement = document.documentElement;
    let currentElement: Element | null = allegedChild;
    while (currentElement !== null && currentElement !== rootElement) {
        if (currentElement === allegedParent) {
            return true;
        }
        currentElement = currentElement.parentElement;
    }
    return false;
}

export const isCompany = (ssn: string | null | undefined) => {
    if(!ssn) return false;
    return ['4','5','6','7'].includes(ssn.charAt(0)) && isSSN(ssn);
}

export const isPerson = (ssn: string | null | undefined) => {
    if (!ssn) return false;
    return ['0','1','2','3'].includes(ssn.charAt(0)) && isSSN(ssn);
}

export const isSSN = (ssn: string | null | undefined, allowEmpty?: boolean) => {
    if (!ssn) {
        return allowEmpty ?? false;
    }
    if (ssn.length === 11 && ssn[6] === '-') {
        ssn = ssn.slice(0, 6) + ssn.slice(7, 11);
    }
    if (ssn.match(/^[\d]{9}[890]$/) === null) {
        return false;
    }
    if (ssn.slice(0, 2) === '00' || ssn.slice(2, 4) === '00')
        return false;
    const checker = parseInt(ssn[8]);
    let sum = 0;
    const multipliers = [3, 2, 7, 6, 5, 4, 3, 2];
    for (let i = 0; i < multipliers.length; i++) {
        sum += parseInt(ssn[i]) * multipliers[i];
    }
    let mod = sum % 11;
    if (mod > 0)
        mod = 11 - mod;
    return checker === mod;
}

export const stripSSN = (ssn: string | null | undefined) => {
    if (ssn && ssn.length === 11 && ssn[6] === '-') {
        return ssn.slice(0, 6) + ssn.slice(7, 11);
    }
    return ssn;
}

export const insertDash = (ssn : string | null | undefined) => {
    if(ssn === null || ssn === undefined) return '-'
    if(ssn.length === 10)
    return ssn.substring(0,6) + '-' + ssn.substring(6)
    else return ssn
}

export function gtmPush(event: string, category: string, action: string, label: string = 'keldan_vefur') {
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    dataLayer.push({
        'event': event,
        'category': category,
        'action': action,
        'label': label
    });
}

export const getSearchTermsFromSearch = (search: string | null) => {
    if (search === null) return [];
    return (search.split(' ')).filter(s => s.length > 0);
}

export const getEventUrl = (searchTerm: string | null, typeName: string | null, eventId: number | null, fileId?: string | null) => {
    const searchTerms = searchTerm?.split(' ') ?? [];

    const switchTypeName = typeName?.replace(/\s$/, "").replace(/\s\s/, ' ') ?? '';

    switch (switchTypeName)
    {
        case "Ársreikningar":
            return `/Pdf/Arsreikningar-Saekja?fileId=${fileId}&ssn=${searchTerms[0]}`;
        case "Samþykktir":
        case "Aukatilkynningar":
        case "Stofngögn":
        case "Samrunagögn":
        case "Hætt við samruna":
            return `/Pdf/Fyrirtaekjaskra?eventId=${eventId}&ssn=${searchTerms[0]}`;
        case "Hlutafélög":
        case "Hlutafélög með heimilisföngum":
        case "Hlutafélög með raunverulegum eigendum":
        case "Hlutafélög með eigendum og heimilisf.":
            return "/Fyrirtaeki/Hlutafelagaskra-Skoda/" + searchTerms[searchTerms.length - 1] + "?eventId=" + eventId;
        case "Fasteignir":
        case "Fasteignir Veðbönd":
            return "/Fasteignir/Fasteignaskra-Skoda/" + eventId;
        case "Fasteignir Eignasaga":
        case "Fasteignir Eignastaða":
            if (isPerson(searchTerms[0])) {
                return "/Einstaklingar/Fasteignir-Skoda/" + eventId;
            }
            return "/Fyrirtaeki/Fasteignir-Skoda/" + stripSSN(searchTerms[0]) + "?eventId=" + eventId;
        case "Fasteignir Verðvísir":
            return "/Fasteignir/Gangverd-Skoda/" + eventId;
        case "Fasteignir Yfirlitsmynd":
            return "/Fasteignir/Yfirlitsmynd-Skoda/" + eventId;
        case "Ökutæki":
        case "Ökutæki Veðbönd":
            return "/Okutaeki/Yfirlit/" + eventId;
        case "Ökutæki Ferilskrá":
            return "/Pdf/VehicleHistory/" + eventId;
        case "Ökutæki Kennitala":
            if (isPerson(searchTerms[0])) {
                return "/Einstaklingar/Okutaeki-Skoda/" + eventId;
            }
            return "/Fyrirtaeki/Okutaeki-Skoda/" + stripSSN(searchTerms[0]) + "?eventId=" + eventId;
        case "Þjóðskrá":
            return "/Einstaklingar/Yfirlit/" + eventId;
        case "Aðild":
            return "/Einstaklingar/Hlutafelagathatttaka-Skoda/" + eventId;
        case "Lögbirtingar":
            return "/Logbirtingar/Skyrsla-Skoda/?eventId=" + eventId;
        case "Vinnuvélar":
            return "/Okutaeki/Vinnuvelaskra-Skoda/" + eventId;
        case "Vinnuvélar Kennitala":
            return "/Fyrirtaeki/Vinnuvelar-Skoda/" + stripSSN(searchTerm) + "?eventId=" + eventId;
        case "Listar":
            return "/Listar/Almennt-Skoda/" + searchTerm?.trim() + "?eventId=" + eventId;
        case "Samanburður skýrsla":
            return "/Fyrirtaeki/Samanburdur-Skoda/" + searchTerms[0] + "?eventId=" + eventId;
        case "Reikningar innslegnir":
            return "/Fyrirtaeki/Innslegnir-Arsreikningar-Skoda/" + searchTerms[searchTerms.length - 1] + "?eventId=" + eventId;
        case "Fyrirmyndarfyrirtæki":
            return "/Listar/Saekja-FyrirmyndarFyrirtaeki/2018";
        default:
            return "/Min-Kelda/Keyptar-Skyrslur";
    }
}
// ICELANDIC LETTER SORTING FUNCTION

// Good to know these charcodes for the corrections.
// a: 97
// e: 101
// i: 105
// o: 111
// u: 117
// y: 121
// z: 122
const CHAR_CODE_CORRECTION: {
    [T in number]: number | undefined
} = {
    225: 97.5,      // á => milli a & b
    240: 100.5,     // ð => milli d & e
    233: 101.5,     // é => milli e & f
    237: 105.5,     // í => milli i & j
    243: 111.5,     // ó => milli o & p
    250: 117.5,     // ú => milli u & v
    253: 121.5,     // ý => milli y & z
    254: 122.2,     // þ => aðeins meira en z
    230: 122.4,     // æ => aðeins meira en þ
    246: 122.6,     // ö => aðeins meira en æ
}

export const sortIcelandic = (a: string | null, b: string | null): number => {
    if (a === null && b === null) return 0;
    if (a === null) return -1;
    if (b === null) return 1;
    const lowercaseA = a.toLowerCase();
    const lowercaseB = b.toLowerCase();
    const charCodeA = lowercaseA.charCodeAt(0);
    const charCodeB = lowercaseB.charCodeAt(0);
    const correctedCharCodeA = CHAR_CODE_CORRECTION[charCodeA] ?? charCodeA;
    const correctedCharCodeB = CHAR_CODE_CORRECTION[charCodeB] ?? charCodeB;
    if (correctedCharCodeA === correctedCharCodeB) return sortIcelandic(a.slice(1) || null, b.slice(1) || null);
    return correctedCharCodeA - correctedCharCodeB;
}

export const getNumberOfPages = (totalCountOfItems: number, itemsPerPage: number) => Math.ceil(totalCountOfItems / itemsPerPage);

export const updateDoubleMapValue = <T extends unknown>(
    doubleMap:  {
        [key1 in string]?: {
            [key2 in string]?: T
        }
    },
    key1: string,
    key2: string,
    newData: T
) => {
    // Copy Double Map.
    const doubleMapCopy = {...doubleMap};
    // Set if empty.
    if (doubleMapCopy?.[key1] === undefined) {
        doubleMapCopy[key1] = {};
    }
    // Copy Single map.
    const nestedMapCopy = {...doubleMapCopy[key1]};
    // Set data.
    nestedMapCopy[key2] = newData;
    // Put the copies back.
    doubleMapCopy[key1] = nestedMapCopy;
    // Return copy.
    return doubleMapCopy;
}

export const icelandifyString = (s: string) => {
    switch(s) { 
        case 'Atvinnuhusnaedi': return 'Atvinnuhúsnæði'
        case 'Fjolbyli': return 'Fjölbýli'
        case 'Serbyli': return 'Sérbýli'
        case 'Sumarhus': return 'Sumarhús'
        case 'Bilskur/skur': return 'Bílskúr/skúr'
        default: return s;
    }
}

export const replaceIcelandicLetters = (s: string) => {
    return s
        //Lowercase
        .replace(/á/g,'a')
        .replace(/ð/g,'d')
        .replace(/é/g,'e')
        .replace(/í/g,'i')
        .replace(/ó/g,'o')
        .replace(/ú/g,'u')
        .replace(/ý/g,'y')
        .replace(/þ/g,'th')
        .replace(/æ/g,'ae')
        .replace(/ö/g,'o')
        //Upper case
        .replace(/Á/g,'A')
        .replace(/Ð/g,'D')
        .replace(/É/g,'E')
        .replace(/Í/g,'I')
        .replace(/Ó/g,'O')
        .replace(/Ú/g,'U')
        .replace(/Ý/g,'Y')
        .replace(/Þ/g,'Th')
        .replace(/Æ/g,'Ae')
        .replace(/Ö/g,'O')
}

export const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

export const idGenerator = (s: string) => {
    return replaceIcelandicLetters(s.toLocaleLowerCase()).replace(/ /g, '_');
}

const shuffle = <T extends unknown>(a: T[]) => {
    let currentIndex = a.length, randomIndex;
    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        // And swap it with the current element.
        [a[currentIndex], a[randomIndex]] = [a[randomIndex], a[currentIndex]];
    }
    return a;
}

const MIN_PWD_LENGTH = 12;
const MAX_PWD_LENGTH = 16;
const PWD_ALPHABET_UPPER = 'AÁBCDEÉFGHIÍJKLMNOÓPQRSTUÚVWZÞÆÖ';
const PWD_ALPHABET_LOWER = 'aábcdeéfghiíjklmnoópqrstuúvwzþæö';
const PWD_ALPHABET_NUMBERS = '1234567890';
let PWD_ALPHABET = [ PWD_ALPHABET_UPPER, PWD_ALPHABET_LOWER, PWD_ALPHABET_NUMBERS ];
export const generatePassword = () => {
    let result = '';
    const length = Math.floor(MIN_PWD_LENGTH + (Math.random() * (MAX_PWD_LENGTH - MIN_PWD_LENGTH)));
    for (let i = 0; i < length/PWD_ALPHABET.length; i++) {
        const shuffled = shuffle(PWD_ALPHABET);
        for (let j = 0; j < shuffled.length; j++) {
            const charset = shuffled[j];
            const nextChar = charset[Math.floor(Math.random() * charset.length)];
            result = result.concat(nextChar);
        }
    }
    return result;
}

export const displayAddress = (address: string | undefined, zip: number | string | undefined, post: string | undefined) => {
    if (address === undefined || address === null) address = '-';
    if (zip === undefined || zip === null) zip = '-';
    if (post === undefined || post === null) post = '-';
    if (address === '-' && zip === '-' && post === '-') return '-';
    return address + ', ' + zip + ' ' + post;
}